import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import {
  Badge,
  Button,
  Card,
  PageHeading,
  Stack,
  Table,
} from "~/components";
import { queryClient } from "~/lib/queryClient";
import {
  childrenOfType,
  workflowsListQueryOptions,
  type Workflow,
} from "~/lib/workflows";

import * as css from "./_app.workflows.index.css";

export const Route = createFileRoute("/_app/workflows/")({
  component: WorkflowsIndexPage,
  loader: () => queryClient.ensureQueryData(workflowsListQueryOptions),
});

function WorkflowsIndexPage() {
  const { data, isPending } = useQuery(workflowsListQueryOptions);

  const heading = (
    <PageHeading
      title="Workflows"
      subtitle="Case-lifecycle definitions used by the engine to drive phase and task state."
      actions={
        <Link to="/workflows/new">
          <Button type="button" variant="primary">
            New workflow
          </Button>
        </Link>
      }
    />
  );

  if (isPending) {
    return (
      <Stack gap="lg">
        {heading}
        <Card>
          <p>Loading workflows…</p>
        </Card>
      </Stack>
    );
  }

  const workflows = data?._embedded ?? [];

  return (
    <Stack gap="lg">
      {heading}
      {workflows.length === 0 ? (
        <Card>
          <Stack gap="md" align="center" className={css.emptyState}>
            <p>No workflows yet.</p>
            <Link to="/workflows/new">
              <Button type="button" variant="primary">
                Create your first workflow
              </Button>
            </Link>
          </Stack>
        </Card>
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phases</th>
                <th>Created</th>
                <th>Updated</th>
                <th className={css.actionsCell} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {workflows.map((wf) => (
                <WorkflowRow key={wf.id} workflow={wf} />
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </Stack>
  );
}

function WorkflowRow({ workflow }: { workflow: Workflow }) {
  const phaseCount = childrenOfType(workflow.definition, "module").length;
  const created = new Date(workflow.created_at).toLocaleDateString();
  const updated = new Date(workflow.updated_at).toLocaleDateString();

  return (
    <tr>
      <td>
        <Link
          to="/workflows/$id/edit"
          params={{ id: workflow.id }}
          className={css.workflowName}
        >
          {workflow.name}
        </Link>
      </td>
      <td>
        <Badge variant="neutral">
          {phaseCount} phase{phaseCount === 1 ? "" : "s"}
        </Badge>
      </td>
      <td className={css.meta}>{created}</td>
      <td className={css.meta}>{updated}</td>
      <td className={css.actionsCell}>
        <div className={css.actions}>
          <Link to="/workflows/$id/edit" params={{ id: workflow.id }}>
            <Button type="button" variant="secondary">
              Edit
            </Button>
          </Link>
        </div>
      </td>
    </tr>
  );
}
